import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { AnalysisSummary } from '../types/acquisitionAnalysis';

export interface UseAnalysisHistory {
  analyses: AnalysisSummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  deleteAnalysis: (analysisId: string) => Promise<void>;
}

interface ListAnalysesRequest {
  companyId: string;
  limit?: number;
  offset?: number;
}

interface ListAnalysesResponse {
  analyses: AnalysisSummary[];
  total: number;
  hasMore: boolean;
}

export function useAnalysisHistory(companyId: string | undefined): UseAnalysisHistory {
  const [analyses, setAnalyses] = useState<AnalysisSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalyses = useCallback(async () => {
    if (!companyId) {
      setIsLoading(false);
      setAnalyses([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const listFunc = httpsCallable<ListAnalysesRequest, ListAnalysesResponse>(
        functions,
        'listAnalyses'
      );
      const response = await listFunc({ companyId });
      const analysesList = response.data.analyses || [];

      setAnalyses(analysesList);
    } catch (err: any) {
      console.error('[Analysis History] Error fetching analyses:', err);
      setError(err);
      // Set empty array on error so UI doesn't stay in loading state
      setAnalyses([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  const deleteAnalysis = useCallback(async (analysisId: string) => {
    if (!companyId) {
      throw new Error('No companyId available');
    }

    try {
      const deleteFunc = httpsCallable<
        { analysisId: string; companyId: string },
        { success: boolean; message: string }
      >(functions, 'deleteAcquisitionAnalysis');

      await deleteFunc({ analysisId, companyId });

      // Remove from local state after successful server delete
      setAnalyses(prev => prev.filter(a => a.analysisId !== analysisId));
    } catch (err: any) {
      console.error('[Analysis History] Error deleting analysis:', err);
      throw err;
    }
  }, [companyId]);

  return {
    analyses,
    isLoading,
    error,
    refetch: fetchAnalyses,
    deleteAnalysis,
  };
}
